import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { getCategoryAndProductId } from 'analytics/snowplow/helpers/itemCategorisation'
import { getAirportsSearch } from 'api/flights'
import { last } from 'lib/array/arrayUtils'
import type { MappedPlace } from 'lib/search/mappingUtils'
import { mapCityLevelPlace } from 'lib/search/mappingUtils'
import { getPlaceByLatLong } from 'api/search'
import type { SelfDescribingJson } from '@snowplow/browser-tracker'
import { createFlightItemContext } from 'analytics/snowplow/events'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { FlightsFareTypes } from 'constants/flight'
import sanitizeItemContextMetadata from './sanitizeItemContextMetadata'
import { getFlightsTotal } from 'lib/flights/flightUtils'
import { floatify } from 'lib/maths/mathUtils'

async function getFlightCartItems(state: App.State, item: App.Checkout.FlightItem, isMultiCart?: boolean) {
  const searchFares = Object.values(state.flights.searchV2Flights)?.find(flights => flights?.searchId === item.searchId)?.fares
  if (!searchFares) return []

  const journeys = item.flights.map((flight, index) => searchFares[index]?.find(fare => fare.id === flight.journeyId))

  const existingJourneys = journeys.filter(journey => journey?.id)
  if (existingJourneys.length !== item.flights.length || journeys.length === 0) return []

  const firstJourney = journeys[0]?.flightGroup
  const firstJourneyFirstFlight = firstJourney?.flights[0]
  const firstJourneyLastFlight = last(firstJourney?.flights || [])
  const lastJourney = last(journeys)?.flightGroup
  const lastJourneyLastFlight = last(lastJourney?.flights || [])

  if (!firstJourneyFirstFlight || !lastJourneyLastFlight || !firstJourneyLastFlight) return []

  const price = floatify(getFlightsTotal(item))
  const { categoryId, productId } = getCategoryAndProductId(item.itemType)

  const isReturnOrOneWay = [FlightsFareTypes.RETURN, FlightsFareTypes.ONE_WAY].includes(item.fareType as FlightsFareTypes)

  const checkoutFirstLeg = item.flights[0]
  const checkoutLastLeg = last(item.flights)
  const travelStart = checkoutFirstLeg?.departingDate ?? firstJourneyFirstFlight.departingDate
  const travelEnd = getCheckoutLegTravelEnd(checkoutLastLeg) ?? lastJourneyLastFlight.arrivalDate

  return [comluxgroup.createItem_1_1_2({
    offerId: 'flight',
    categoryId,
    productId,
    duration: getFlightDurationInDays(travelStart, travelEnd),
    travelStart,
    travelEnd,
    numberOfAdults: item.occupants.adults,
    numberOfChildren: item.occupants.children,
    numberOfInfants: item.occupants.infants,
    childrenAges: item.occupants.childrenAge,
    currency: state.geo.currentCurrency,
    destinationCountry: isReturnOrOneWay ? firstJourneyLastFlight.arrivalCountry : lastJourneyLastFlight.arrivalCountry,
    originCountry: firstJourneyFirstFlight.departureCountry,
    originCity: firstJourneyFirstFlight.departureCity,
    destinationCity: isReturnOrOneWay ? firstJourneyLastFlight.arrivalCity : lastJourneyLastFlight.arrivalCity,
    typeId: item.fareType.toLowerCase(),
    itemId: item.itemId,
    price,
    metadata: sanitizeItemContextMetadata({
      isMultiCart,
      originAirportCode: checkoutFirstLeg?.departingAirportCode ?? firstJourneyFirstFlight.departureAirport,
      arrivalAirportCode: (isReturnOrOneWay
        ? checkoutFirstLeg?.arrivalAirportCode
        : checkoutLastLeg?.arrivalAirportCode) ??
        (isReturnOrOneWay ? firstJourneyFirstFlight.arrivalAirport : lastJourneyLastFlight.arrivalAirport),
    }),
  })]
}

function getCheckoutLegTravelEnd(flight?: App.Checkout.FlightItemFlight) {
  if (!flight) return undefined

  if (flight.arrivalDateTime) {
    return moment(flight.arrivalDateTime).format(ISO_DATE_FORMAT)
  }

  return flight.departingDate || undefined
}

function getFlightDurationInDays(departureDate?: string, arrivalDate?: string) {
  // default to 1 for one way
  const calculatedDuration = departureDate && arrivalDate ? moment(arrivalDate).diff(departureDate, 'days') : 1
  return calculatedDuration && calculatedDuration > 0 ? calculatedDuration : 1
}

async function getCityByAirportName(departingAirportName: string, arrivalAirportName: string) {
  const [departingAirportData, arrivalAirportData] = await Promise.all([getAirportsSearch(departingAirportName), getAirportsSearch(arrivalAirportName)])

  const departingLongitude = departingAirportData[0]?.lg
  const departingLatitude = departingAirportData[0]?.lt
  const arrivalLongitude = arrivalAirportData[0]?.lg
  const arrivalLatitude = arrivalAirportData[0]?.lt

  const getCitiesPromises: Array<Promise<MappedPlace | undefined>> = []

  getCitiesPromises.push(
    departingLongitude && departingLatitude
      ? getPlaceByLatLong(departingLatitude, departingLongitude).then(mapCityLevelPlace)
      : Promise.resolve(undefined),
  )

  getCitiesPromises.push(
    arrivalLongitude && arrivalLatitude
      ? getPlaceByLatLong(arrivalLatitude, arrivalLongitude).then(mapCityLevelPlace)
      : Promise.resolve(undefined),
  )

  const places = await Promise.all(getCitiesPromises)

  return {
    originCity: places[0]?.city,
    destinationCity: places[1]?.city,
  }
}

export async function journeyToSnowplowItemContext(journey: App.JourneyV2, currency: string, occupancy: App.Occupants): Promise<SelfDescribingJson> {
  const firstFlight = journey.flightGroup.flights[0]
  const lastFlight = last(journey.flightGroup.flights)

  let cities: {
    originCity: string | undefined
    destinationCity: string | undefined
  } = { originCity: undefined, destinationCity: undefined }

  if (firstFlight?.departureAirportName && lastFlight?.arrivalAirportName) {
    try {
      cities = await getCityByAirportName(firstFlight?.departureAirportName, lastFlight?.arrivalAirportName)
    } catch { }
  }

  const flightItemContext = createFlightItemContext({
    offerId: 'flight',
    originCity: cities?.originCity,
    destinationCity: cities?.destinationCity,
    airline: journey.carrier,
    arrivalAirportCode: lastFlight.arrivalAirport,
    departureDate: firstFlight.departingDate,
    duration: getFlightDurationInDays(firstFlight.departingDate, lastFlight.arrivalDate),
    fareType: journey.fareType.toLowerCase(),
    numberOfAdults: occupancy.adults,
    numberOfChildren: occupancy.children,
    numberOfInfants: occupancy.infants,
    childrenAges: occupancy.childrenAge,
    originAirportCode: firstFlight.departureAirport,
    pricePerAdult: journey.price.adult.totalFare!,
    arrivalDate: lastFlight.arrivalDate,
    destinationCountry: lastFlight.arrivalCountry,
    originCountry: firstFlight.departureCountry,
    currency,
  })

  return flightItemContext
}

export default getFlightCartItems
